// The height profile: distance along, metres up. Drag a finger along it to read the height and
// the distance at any point (every number is a handle). Optional marks, like the dark stretch.
import { useRef, useState } from "preact/hooks";

export function Profile({ points, marks = [], label, h = 120 }: { points: [number, number][]; marks?: { from: number; to: number; cls: string }[]; label: string; h?: number }) {
  const [at, setAt] = useState<number | null>(null);
  const ref = useRef<SVGSVGElement>(null);
  if (points.length < 2) return null;
  const W = 360, H = h, top = 14, bottom = 18;
  const km = points[points.length - 1][0];
  const lo = Math.min(...points.map((p) => p[1])), hi = Math.max(...points.map((p) => p[1]));
  const span = Math.max(60, hi - lo);
  const x = (k: number) => (k / km) * W;
  const y = (m: number) => top + (1 - (m - lo) / span) * (H - top - bottom);
  const line = points.map((p, i) => `${i ? "L" : "M"}${x(p[0]).toFixed(1)} ${y(p[1]).toFixed(1)}`).join("");
  const area = `${line}L${W} ${H - bottom}L0 ${H - bottom}Z`;
  const pick = (e: PointerEvent) => {
    const r = ref.current!.getBoundingClientRect();
    const k = Math.max(0, Math.min(km, ((e.clientX - r.left) / r.width) * km));
    setAt(k);
  };
  const near = at == null ? null : points.reduce((b, p) => (Math.abs(p[0] - at) < Math.abs(b[0] - at) ? p : b), points[0]);
  const ticks = (km > 120 ? [50, 100, 150, 200] : km > 40 ? [20, 40, 60, 80, 100] : [10, 20, 30]).filter((t) => t < km * 0.9);
  return (
    <div class="profile">
      <svg ref={ref} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={label}
        onPointerDown={(e) => { (e.currentTarget as Element).setPointerCapture(e.pointerId); pick(e); }}
        onPointerMove={(e) => { if ((e.buttons & 1) || e.pointerType === "touch") pick(e); }}
        onPointerUp={() => setTimeout(() => setAt(null), 1600)}>
        {marks.map((m) => <rect x={x(m.from)} y={0} width={Math.max(1, x(m.to) - x(m.from))} height={H - bottom} class={m.cls} />)}
        <path d={area} class="pf-area" />
        <path d={line} class="pf-line" vector-effect="non-scaling-stroke" />
        {ticks.map((t) => <line x1={x(t)} x2={x(t)} y1={H - bottom} y2={H - bottom + 4} class="pf-tick" vector-effect="non-scaling-stroke" />)}
        {near && <line x1={x(near[0])} x2={x(near[0])} y1={0} y2={H - bottom} class="pf-cursor" vector-effect="non-scaling-stroke" />}
      </svg>
      <div class="pf-labels"><span class="label">0</span>{ticks.map((t) => <span class="label" style={`left:${(t / km) * 100}%`}>{t}</span>)}<span class="label end">{Math.round(km)} km</span></div>
      <div class="row-between pf-read">
        <span class="label">{near ? `${near[0].toFixed(1)} km` : "Drag to read"}</span>
        <span class="label ink">{near ? `${near[1]} m` : `${lo} to ${hi} m`}</span>
      </div>
    </div>
  );
}
